/**
 * What each unanswered question would buy.
 *
 * Deliberately NOT a completion percentage. "Profile 47% complete" tells you
 * nothing about what to do next; it does not say which of the remaining fields
 * matters or whether any of them do. This states a trade instead — this field,
 * for these obligations — so ten seconds spent typing turnover is visibly worth
 * more than ten minutes spent on everything else.
 *
 * PLACEHOLDER SOURCE. The obligation lists below are hardcoded from the
 * Corporate Compliance Framework. They are what the rule book will hold once
 * task 6 exists, at which point this table is replaced by a query against it
 * and the counts become live rather than illustrative. The shape of what this
 * module returns is designed not to change when that happens.
 */

/** A stored 0 reads as unknown, not as a genuine nil — MCA publishes 0 when an
 *  amount is withheld, and the deadline engine already treats it that way. */
const hasAmount = (v) => {
  if (v === '' || v === null || v === undefined) return false;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) && n > 0;
};

const seriesLength = (rows) => (Array.isArray(rows) ? rows.filter(
  (r) => r && r.effectiveFrom && r.value !== '' && r.value !== null && r.value !== undefined
).length : 0);

/** At least one premises with a head count on it. Company-wide totals do not
 *  count: EPF and ESI test employees AT a premises, so a single national figure
 *  cannot answer either test. */
const hasPremisesHeadcount = (form) => (Array.isArray(form.establishments) ? form.establishments : [])
  .some((e) => e && String(e.state || '').trim() && hasAmount(e.employeeCount));

export const RESOLVERS = [
  {
    key: 'turnover',
    label: 'Turnover',
    section: 'financials',
    obligations: ['MGT-7 or MGT-7A', 'CARO', 'Internal financial controls', 'Secretarial audit (MR-3)', 'XBRL'],
    isAnswered: (f) => hasAmount(f.turnover),
    note: 'One figure. MCA never publishes it, so it has to be typed.',
  },
  {
    key: 'workforce',
    label: 'Employees and premises',
    section: 'establishments',
    obligations: ['EPF', 'ESI', 'POSH', 'Gratuity'],
    isAnswered: hasPremisesHeadcount,
    note: 'Head count at each premises, not one company-wide total.',
  },
  {
    key: 'netWorthProfit',
    label: 'Net worth and net profit',
    section: 'financials',
    // Section 135 tests the AVERAGE of the three preceding financial years, so
    // one year is not an answer — a company with two years of history genuinely
    // cannot be determined yet, and this must say so rather than count it done.
    obligations: ['CSR (Section 135)'],
    isAnswered: (f) => seriesLength(f.netWorthHistory) >= 1 && seriesLength(f.netProfitHistory) >= 3,
    note: 'Section 135 averages the three preceding years, so three rows are needed.',
  },
];

/**
 * @returns {{
 *   unresolvedCount: number,      distinct obligations still unsettled
 *   resolvedCount: number,        distinct obligations these answers settle
 *   items: Array<{key,label,section,obligations,resolves,isAnswered,note}>,
 *   allAnswered: boolean
 * }}
 */
export function computeProfileCompleteness(form = {}) {
  const items = RESOLVERS.map((r) => ({
    key: r.key,
    label: r.label,
    section: r.section,
    obligations: r.obligations,
    resolves: r.obligations.length,
    note: r.note,
    isAnswered: Boolean(r.isAnswered(form)),
  }));

  // Distinct, so an obligation named by two resolvers is not counted twice.
  const unresolved = new Set();
  const resolved = new Set();
  for (const it of items) {
    for (const o of it.obligations) (it.isAnswered ? resolved : unresolved).add(o);
  }
  // An obligation settled by one answer is settled, even if another resolver
  // that also names it is still blank.
  for (const o of resolved) unresolved.delete(o);

  return {
    unresolvedCount: unresolved.size,
    resolvedCount: resolved.size,
    items,
    allAnswered: items.every((i) => i.isAnswered),
  };
}
